"use client"

import { useEffect, useRef, useState, type JSX } from "react"
import {
  listSessions,
  renameSession,
  getSession,
  type Session,
} from "@/services/sessionsService"
import { useAuth } from "@/hooks/useAuth"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronDown,
  ChevronRight,
  CircleGauge,
  Info,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DEFAULT_AGENT_NAME,
  useAgentCoreClient,
  type AgentName,
} from "@/lib/agentcore-client"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx"
import LoadingSpinner from "../shared/LoadingSpinner"

/** Props for the ChatInfoDialog component. */
interface ChatInfoDialogProps {
  /** The session ID to fetch info for, or null if the dialog should be closed. */
  sessionId: string | null
  /** Callback fired when the dialog requests to close. */
  onClose: () => void
  /** The current assistant, used to gate system-prompt loading. */
  agent?: AgentName
}

/**
 * A centered popup card displaying metadata about a chat session.
 * Fetches fresh session data from the backend each time it opens to ensure
 * token usage counts are up-to-date. Closes when clicking the X button or
 * clicking outside the card.
 *
 * @param sessionId - The session ID to display info for, or null to hide the dialog.
 * @param onClose   - Called when the user dismisses the dialog.
 * @param agent     - The current assistant whose selected client provides the prompt.
 * @returns The rendered dialog element.
 */
function ChatInfoDialog({ sessionId, onClose, agent }: ChatInfoDialogProps): JSX.Element {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [promptOpen, setPromptOpen] = useState<boolean>(false)
  const [prompt, setPrompt] = useState<string | null>(null)
  const [promptLoading, setPromptLoading] = useState<boolean>(false)
  const [promptError, setPromptError] = useState<string | null>(null)
  const { getAccessToken } = useAuth()
  const client = useAgentCoreClient()

  // Fetch the agent's system prompt the first time the section is expanded.
  useEffect(() => {
    if (!promptOpen || !agent || !client || prompt !== null) return

    let cancelled = false

    async function fetchPrompt(): Promise<void> {
      setPromptLoading(true)
      setPromptError(null)
      try {
        const token = await getAccessToken()
        if (!token || cancelled) return
        const text = await client!.getSystemPrompt(token)
        if (!cancelled) setPrompt(text)
      } catch (err) {
        if (!cancelled) {
          setPromptError(err instanceof Error ? err.message : "Failed to load prompt")
        }
      } finally {
        if (!cancelled) setPromptLoading(false)
      }
    }

    fetchPrompt()

    return () => { cancelled = true }
  }, [promptOpen, agent, client, prompt, getAccessToken])

  // Reset the fetched prompt when the dialog switches to a different session,
  // so a stale prompt from a previous agent is never shown.
  useEffect(() => {
    setPrompt(null)
    setPromptOpen(false)
    setPromptError(null)
  }, [sessionId])

  // Fetch fresh session data whenever the dialog opens (sessionId changes to non-null)
  useEffect(() => {
    if (!sessionId) {
      setSession(null)
      return
    }

    let cancelled = false

    async function fetchSession(): Promise<void> {
      setLoading(true)
      try {
        const token = await getAccessToken()
        if (!token || cancelled) return
        const data = await getSession(sessionId!, token)
        if (!cancelled) setSession(data)
      } catch (err) {
        console.error("Failed to fetch session info:", err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSession()

    return () => { cancelled = true }
  }, [sessionId, getAccessToken])

  return (
    <Dialog
      open={sessionId !== null}
      onOpenChange={(open: boolean) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        className="sm:max-w-md md:max-w-3xl p-0 overflow-hidden gap-0"
        aria-describedby={undefined}
      >
        <div className="bg-muted px-6 py-4 flex items-center gap-3">
          <div className="flex items-center justify-center rounded-full bg-primary p-2">
            <Info className="h-4 w-4 text-primary-foreground" />
          </div>
          <DialogHeader className="p-0 gap-0">
            <DialogTitle className="text-foreground text-base leading-tight">
              Conversation statistics
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-3">
          {loading ? (
            <LoadingSpinner loadingContent="Loading session data..." variant="default" />
          ) : (
            <>
              <div>
                {session?.title && (
                  <p className="text-xs mb-4">
                    <p className="font-semibold uppercase tracking-wider text-muted-foreground">
                      Title
                    </p>
                    <span className="">{session.title}</span>
                  </p>
                )}

                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Token Usage
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <ArrowDownToLine className="h-3 w-3" />
                      <span className="text-[10px] font-medium uppercase tracking-wide">Input</span>
                    </div>
                    <span className="text-base font-bold tabular-nums">
                      {session?.inputTokens?.toLocaleString() ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <ArrowUpFromLine className="h-3 w-3" />
                      <span className="text-[10px] font-medium uppercase tracking-wide">
                        Output
                      </span>
                    </div>
                    <span className="text-base font-bold tabular-nums">
                      {session?.outputTokens?.toLocaleString() ?? "—"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleGauge className="h-3 w-3" />
                      <span className="text-[10px] font-medium uppercase tracking-wide">Total</span>
                    </div>
                    <span className="text-base font-bold tabular-nums text-primary">
                      {session?.totalTokens?.toLocaleString() ?? "—"}
                    </span>
                  </div>
                </div>
              </div>

              {agent && (
                <div className="overflow-hidden">
                  <Collapsible open={promptOpen} onOpenChange={setPromptOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:bg-muted/50 transition-colors cursor-pointer">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        View Agent Prompt
                      </span>
                      {promptOpen ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t bg-muted/30">
                        {promptLoading ? (
                          <LoadingSpinner loadingContent="Loading prompt..." variant="default" />
                        ) : promptError ? (
                          <div className="px-4 py-3 text-xs text-destructive">{promptError}</div>
                        ) : (
                          <pre className="max-h-70 overflow-auto px-4 py-3 text-xs whitespace-pre-wrap wrap-break-word font-mono leading-relaxed">
                            {prompt ?? "No prompt available"}
                          </pre>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface ChatSessionsSidebarProps {
  /** The currently active session ID (highlighted in the list). */
  activeSessionId: string
  /** Called when the user clicks a session to resume it. */
  onSelectSession: (sessionId: string) => void
  /** Called when the user deletes a session (after API call succeeds). */
  onDeleteSession: (sessionId: string) => Promise<void>
  /** Called when the active session is no longer in the list (e.g., deleted from another tab). */
  onActiveSessionGone?: () => void
  /** Incremented to trigger a refresh of the sessions list. */
  refreshTrigger: number
  /** Name of the agent whose sessions should be shown. */
  agent?: AgentName
  /** Called when the user clicks the "New Chat" button. */
  onNewChat: () => void
  /** Whether the "New Chat" button should be enabled. */
  canStartNewChat: boolean
}

/**
 * Scrollable sidebar displaying the authenticated user's chat sessions
 * ordered by most recently active. Only rendered when VITE_ENVIRONMENT is "dev".
 *
 *
 * @param activeSessionId    - The session currently in use (visually highlighted).
 * @param onSelectSession    - Callback to switch to a different session.
 * @param onDeleteSession    - Callback to delete a session (async, awaited for loading state).
 * @param onActiveSessionGone - Called when the active session disappears from the list on refresh.
 * @param refreshTrigger     - Changing this value forces a re-fetch of sessions.
 * @param agent              - Name of the agent whose sessions to display.
 * @param onNewChat          - Callback fired when the user clicks the "New Chat" button.
 * @param canStartNewChat    - Whether the "New Chat" button should be enabled.
 */
export function ChatSessionsSidebar({
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onActiveSessionGone,
  refreshTrigger,
  agent = DEFAULT_AGENT_NAME,
  onNewChat,
  canStartNewChat,
}: ChatSessionsSidebarProps) {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [infoSessionId, setInfoSessionId] = useState<string | null>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const { getAccessToken } = useAuth()
  const defaultSidebarOpen = typeof window !== "undefined" ? window.innerWidth >= 768 : true
  const sidebarTitle: string = "Conversation History"


  /** Submit the rename and refresh the sidebar. */
  async function handleRenameSubmit(sessionId: string) {
    const trimmed = renameValue.trim()
    if (!trimmed) {
      setRenamingSessionId(null)
      return
    }
    try {
      const token = await getAccessToken()
      if (!token) return
      await renameSession(sessionId, trimmed, token)
      // Update locally for instant feedback
      setSessions((prev) =>
        prev.map((s) => (s.sessionId === sessionId ? { ...s, title: trimmed } : s))
      )
    } catch (err) {
      console.error("Failed to rename session:", err)
    } finally {
      setRenamingSessionId(null)
    }
  }

  useEffect(() => {
    async function fetchSessions() {
      try {
        const token = await getAccessToken()
        if (!token) return
        const data = await listSessions(agent, token)
        setSessions(data)
      } catch (err) {
        console.error("Failed to load sessions:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchSessions()
  }, [refreshTrigger, agent, getAccessToken])

  // Refresh sidebar when the tab becomes visible (handles stale tabs,
  // laptop wake, alt-tab back from another app/window)
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === "visible") {
        try {
          const token = await getAccessToken()
          if (!token) return
          const data = await listSessions(agent, token)

          // Only fire onActiveSessionGone if the session was previously in the
          // list (i.e., it was persisted) but is now missing. New sessions that
          // haven't been persisted yet won't be in either list.
          const wasInList = sessions.some((s) => s.sessionId === activeSessionId)
          const isInList = data.some((s) => s.sessionId === activeSessionId)
          if (wasInList && !isInList) {
            onActiveSessionGone?.()
          }

          setSessions(data)
        } catch (err) {
          console.error("Failed to refresh sessions:", err)
        }
      }
    }
    document.addEventListener("visibilitychange", handleVisibility)
    return () => document.removeEventListener("visibilitychange", handleVisibility)
  }, [agent, activeSessionId, onActiveSessionGone, sessions, getAccessToken])

  // Focus the rename input when it appears
  useEffect(() => {
    if (renamingSessionId) {
      renameInputRef.current?.focus()
    }
  }, [renamingSessionId])

  return (
    <SecondarySideBar
      defaultOpen={defaultSidebarOpen}
      collapsedContent={
        <>
          {sessions.map(session => (
            <button
              key={session.sessionId}
              onClick={() => onSelectSession(session.sessionId)}
              title={session.title || "Untitled conversation"}
              className={`flex items-center justify-center w-8 h-8 rounded hover:bg-muted transition-colors cursor-pointer ${
                session.sessionId === activeSessionId ? "bg-muted" : ""
              }`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  {session.title || "Untitled conversation"}
                </TooltipContent>
              </Tooltip>
            </button>
          ))}
        </>
      }
      collapsedFooter={
        <button
          onClick={onNewChat}
          disabled={!canStartNewChat}
          title="New Conversation"
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="right">New Conversation</TooltipContent>
          </Tooltip>
        </button>
      }
      sidebarTitle={sidebarTitle}
    >
      <div
        className="flex flex-col min-h-0 flex-1 overflow-hidden"
      >
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="p-3 text-sm text-muted-foreground">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No previous sessions</div>
          ) : (
            <ul className="py-1">
              {sessions.map(session => (
                <li key={session.sessionId} className="group relative">
                  <button
                    onClick={() => onSelectSession(session.sessionId)}
                    title={session.title || "Untitled conversation"}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors flex items-start gap-2 ${
                      session.sessionId === activeSessionId ? "bg-muted font-medium" : ""
                    }`}
                  >
                    <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1 group-hover:pr-6">
                      {renamingSessionId === session.sessionId ? (
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => handleRenameSubmit(session.sessionId)}
                          onKeyDown={e => {
                            if (e.key === "Enter") handleRenameSubmit(session.sessionId)
                            if (e.key === "Escape") setRenamingSessionId(null)
                          }}
                          className="w-full text-sm bg-transparent border-b border-foreground/30 outline-none py-0.5"
                          style={{ fontSize: "13px" }}
                        />
                      ) : (
                        <p className="truncate" style={{ fontSize: "13px" }}>
                          {session.title || "Untitled conversation"}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {new Date(session.lastModifiedAt).toLocaleString([], {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                        {session.expiresAt &&
                          (() => {
                            const daysLeft = Math.max(
                              0,
                              Math.ceil(
                                (Number(session.expiresAt) * 1000 - Date.now()) /
                                  (1000 * 60 * 60 * 24)
                              )
                            )
                            return <span className="ml-1">· {daysLeft}d left</span>
                          })()}
                      </p>
                    </div>
                  </button>

                  {/* Meatball menu — visible on hover */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        onClick={e => e.stopPropagation()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-muted-foreground/10 transition-opacity"
                        aria-label="Session options"
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-36 p-1 gap-0" sideOffset={4}>
                      <button
                        onClick={() => {
                          setRenamingSessionId(session.sessionId)
                          setRenameValue(session.title || "")
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Rename
                      </button>
                      <button
                        onClick={() => setInfoSessionId(session.sessionId)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors"
                      >
                        <Info className="h-3.5 w-3.5" />
                        Info
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-destructive transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete chat</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete &quot;
                              {session.title || "Untitled conversation"}&quot;? This cannot be
                              undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDeleteSession(session.sessionId)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </PopoverContent>
                  </Popover>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t shrink-0">
          <Button
            onClick={onNewChat}
            variant="outline"
            size="sm"
            className="w-full gap-2"
            disabled={!canStartNewChat}
          >
            <Plus className="h-4 w-4" />
            New Conversation
          </Button>
        </div>
      </div>

      <ChatInfoDialog
        sessionId={infoSessionId}
        onClose={() => setInfoSessionId(null)}
        agent={agent}
      />
    </SecondarySideBar>
  )
}
